"use client"

import { useState, useEffect } from "react"
import { useSearchParams, useRouter } from "next/navigation"
import Link from "next/link"
import { motion } from "framer-motion"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { signIn, getSession } from "next-auth/react"
import {
  Building,
  Mail,
  Globe,
  User,
  CreditCard,
  CheckCircle,
  UserCheck,
  ArrowRight,
  Shield,
  Layers,
  Combine,
  Lock,
  Eye,
  EyeOff,
  MailCheck
} from "lucide-react"
import { ThemeToggle } from "@/components/theme-toggle"

export function MerchantSignupClient() {
  const [formData, setFormData] = useState({
    businessName: "",
    businessEmail: "",
    website: "",
    contactName: "",
    description: "",
    serviceMode: "MANAGED"
  })
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [step, setStep] = useState(1) // 1 = mode selection, 2 = form
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [domainError, setDomainError] = useState<string>('')
  const [affiliateInfo, setAffiliateInfo] = useState<any>(null)
  const [referralCode, setReferralCode] = useState("")
  const [registered, setRegistered] = useState(false)
  const [error, setError] = useState("")
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState("")
  const { toast } = useToast()
  const router = useRouter()
  const searchParams = useSearchParams()

  useEffect(() => {
    // Check for referral code in URL
    const ref = searchParams.get('ref')
    if (ref) {
      setReferralCode(ref)
      fetchAffiliateInfo(ref)
    }

    // Check if user is already logged in
    checkSession()
  }, [searchParams])

  const checkSession = async () => {
    const session = await getSession()
    if (session) {
      router.push('/dashboard')
    }
  }

  const FREE_EMAIL_DOMAINS = new Set([
    "gmail.com", "googlemail.com", "yahoo.com", "yahoo.co.uk", "hotmail.com",
    "hotmail.co.uk", "outlook.com", "live.com", "msn.com", "aol.com",
    "icloud.com", "me.com", "protonmail.com", "proton.me", "zoho.com",
    "yandex.com", "mail.com", "gmx.com",
  ])

  function validateDomain(email: string, website: string): string {
    if (!email || !website) return ''
    const emailDomain = email.split('@')[1]?.toLowerCase() || ''
    if (FREE_EMAIL_DOMAINS.has(emailDomain)) {
      return 'Please use a business email address. Free email providers (Gmail, Yahoo, etc.) are not accepted.'
    }
    try {
      const webDomain = new URL(website.startsWith('http') ? website : `https://${website}`).hostname.replace(/^www\./, '').toLowerCase()
      if (emailDomain && webDomain && emailDomain !== webDomain && !emailDomain.endsWith(`.${webDomain}`)) {
        return 'Your email domain must match your business website domain.'
      }
    } catch {}
    return ''
  }

  useEffect(() => {
    const error = validateDomain(formData.businessEmail, formData.website)
    setDomainError(error)
  }, [formData.businessEmail, formData.website])

  const fetchAffiliateInfo = async (code: string) => {
    try {
      const response = await fetch(`/api/public/affiliate/${code}`)
      if (response.ok) {
        const data = await response.json()
        setAffiliateInfo(data.affiliate)
      }
    } catch (error) {
      console.error("Error fetching affiliate info:", error)
    }
  }

  // Email/password signup
  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!formData.businessName || !formData.businessEmail || !formData.contactName) {
      setError("Business name, email, and contact name are required")
      return
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters")
      return
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    // Client-side domain check
    const domainErr = validateDomain(formData.businessEmail, formData.website)
    if (domainErr) {
      setError(domainErr)
      return
    }

    setLoading(true)

    try {
      const res = await fetch('/api/signup/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: formData.businessName,
          businessEmail: formData.businessEmail,
          contactName: formData.contactName,
          website: formData.website,
          description: formData.description,
          password,
          referralCode: referralCode || null,
          serviceMode: formData.serviceMode,
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || "Registration failed. Please try again.")
        return
      }

      setRegistered(true)
    } catch (err) {
      console.error("Signup error:", err)
      setError("An unexpected error occurred. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  // Google OAuth signup (existing flow)
  const handleGoogleSignup = async () => {
    setGoogleLoading(true)
    setError("")

    try {
      if (!formData.businessName || !formData.businessEmail || !formData.contactName) {
        setError("Please fill in business name, email, and contact name before continuing with Google")
        setGoogleLoading(false)
        return
      }

      const domainErr = validateDomain(formData.businessEmail, formData.website)
      if (domainErr) {
        setError(domainErr)
        setGoogleLoading(false)
        return
      }

      // Store signup data for after OAuth
      const signupData = {
        ...formData,
        referralCode: referralCode || null,
        submittedAt: new Date().toISOString()
      }
      sessionStorage.setItem('pendingSignup', JSON.stringify(signupData))

      await signIn('google', {
        callbackUrl: '/signup/complete',
        redirect: true
      })
    } catch (error) {
      console.error("Error during Google signup:", error)
      setError("Failed to start Google signup. Please try again.")
      setGoogleLoading(false)
    }
  }

  const handleResend = async () => {
    setResendLoading(true)
    setResendMessage("")
    try {
      await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.businessEmail }),
      })
      setResendMessage("Verification email resent. Please check your inbox.")
    } catch {
      setResendMessage("Failed to resend. Please try again.")
    } finally {
      setResendLoading(false)
    }
  }

  // Email verification success screen
  if (registered) {
    return (
      <div className="min-h-screen bg-fintech flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="bg-background border-border shadow-xl">
            <CardContent className="pt-8 pb-8">
              <div className="text-center space-y-4">
                <div className="flex justify-center mb-2">
                  <div className="w-16 h-16 rounded-full bg-fluxCyan-400/10 border border-fluxCyan-400/20 flex items-center justify-center">
                    <MailCheck className="w-8 h-8 text-fluxCyan-400" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-foreground">Check your email</h2>
                <p className="text-muted-foreground">
                  We sent a verification link to
                </p>
                <p className="text-fluxCyan-400 font-medium">{formData.businessEmail}</p>
                <p className="text-muted-foreground text-sm">
                  Click the link in the email to verify your account. The link expires in 24 hours.
                </p>
                {resendMessage && (
                  <p className="text-sm text-green-400">{resendMessage}</p>
                )}
                <button
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="text-sm text-muted-foreground hover:text-fluxCyan-400 transition-colors disabled:opacity-50"
                >
                  {resendLoading ? "Sending..." : "Resend verification email"}
                </button>
                <div className="pt-4">
                  <Button
                    onClick={() => router.push('/auth/signin')}
                    className="w-full"
                    size="lg"
                  >
                    Go to Sign In
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-fintech">
      <motion.div animate={{ x: [0, 65, 0], y: [0, -30, 0] }} transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }} className="pointer-events-none fixed -left-56 -top-56 h-[560px] w-[560px] rounded-full bg-[#3b82f6]/12 blur-[130px]" />
      <motion.div animate={{ x: [0, -50, 0], y: [0, 40, 0] }} transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }} className="pointer-events-none fixed -right-48 top-1/3 h-[520px] w-[520px] rounded-full bg-[#8b5cf6]/12 blur-[140px]" />
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between p-5 sm:p-8"><Link href="/" className="brand-glass flex items-center gap-2.5 rounded-xl py-2 pl-2 pr-4 transition hover:-translate-y-0.5"><img src="/favicon.svg" alt="FluxPay" className="h-9 w-9" /><span className="text-sm font-bold uppercase tracking-[.14em]">FluxPay</span></Link><ThemeToggle variant="segmented" /></div>
      <div className="container relative mx-auto px-5 pb-16 pt-32 sm:px-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 24, filter: 'blur(10px)' }} animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }} transition={{ duration: .7, ease: [0.16, 1, 0.3, 1] }} className="mb-12 text-center">
          <div className="mb-5 flex items-center justify-center">
            <div className="rounded-2xl border border-blue-400/25 bg-gradient-to-br from-blue-500/15 to-cyan-500/10 p-3 shadow-[0_16px_48px_-24px_rgba(59,130,246,.8)]">
              <Building className="h-7 w-7 text-blue-400" />
            </div>
          </div>
          <p className="product-kicker mb-4">Merchant onboarding</p>
          <h1 className="mx-auto mb-4 max-w-4xl text-4xl font-semibold leading-[1.02] tracking-[-.045em] text-foreground sm:text-5xl lg:text-6xl">
            Start Accepting Payments Today
          </h1>
          <p className="mx-auto max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
            Connect providers, orchestrate routes and operate payments through one control plane.
          </p>
        </motion.div>

        {/* Referral Alert */}
        {affiliateInfo && (
          <div className="max-w-2xl mx-auto mb-8">
            <Card className="border-fluxCyan-400/30 bg-fluxCyan-400/10">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-3">
                  <UserCheck className="h-6 w-6 text-fluxCyan-400" />
                  <div>
                    <p className="font-medium text-foreground">
                      You're signing up through {affiliateInfo.user.name}'s referral!
                    </p>
                    <p className="text-sm text-muted-foreground">
                      You'll be connected with a trusted FluxPay partner who can help you get started.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 1: Service Mode Selection */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .12, duration: .65 }} className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="mb-2 text-2xl font-semibold tracking-[-.025em] text-foreground">Choose your service mode</h2>
              <p className="text-sm text-muted-foreground">Select the operating model that matches your provider setup. You can change it later.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-10">
              {/* Orchestration */}
              <div
                onClick={() => setFormData(prev => ({ ...prev, serviceMode: "ORCHESTRATION" }))}
                className={`cursor-pointer rounded-2xl border p-6 transition-all duration-200 hover:-translate-y-1 ${
                  formData.serviceMode === "ORCHESTRATION"
                    ? "border-blue-400/60 bg-blue-500/10 shadow-[0_20px_55px_-32px_rgba(59,130,246,.9)] ring-1 ring-blue-400/30"
                    : "border-border/80 bg-card/70 hover:border-blue-400/35"
                }`}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="rounded-xl border border-blue-400/25 bg-blue-400/10 p-3">
                    <Layers className="h-8 w-8 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">Orchestration</h3>
                    <p className="text-sm font-medium text-blue-400 mb-2">Bring Your Own PSP</p>
                    <p className="text-sm text-muted-foreground">
                      Connect your existing payment providers and route intelligently across them
                    </p>
                  </div>
                </div>
              </div>

              {/* Managed PSP */}
              <div
                onClick={() => setFormData(prev => ({ ...prev, serviceMode: "MANAGED" }))}
                className={`cursor-pointer rounded-2xl border p-6 transition-all duration-200 hover:-translate-y-1 ${
                  formData.serviceMode === "MANAGED"
                    ? "border-cyan-400/60 bg-cyan-400/10 shadow-[0_20px_55px_-32px_rgba(6,182,212,.9)] ring-1 ring-cyan-400/30"
                    : "border-border/80 bg-card/70 hover:border-cyan-400/35"
                }`}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/10 p-3">
                    <Shield className="h-8 w-8 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">Managed PSP</h3>
                    <p className="text-sm font-medium text-cyan-400 mb-2">We Handle Everything</p>
                    <p className="text-sm text-muted-foreground">
                      Start processing immediately using our provider network and MSB license
                    </p>
                  </div>
                </div>
              </div>

              {/* Hybrid */}
              <div
                onClick={() => setFormData(prev => ({ ...prev, serviceMode: "HYBRID" }))}
                className={`cursor-pointer rounded-2xl border p-6 transition-all duration-200 hover:-translate-y-1 ${
                  formData.serviceMode === "HYBRID"
                    ? "border-purple-400/60 bg-purple-400/10 shadow-[0_20px_55px_-32px_rgba(139,92,246,.9)] ring-1 ring-purple-400/30"
                    : "border-border/80 bg-card/70 hover:border-purple-400/35"
                }`}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="rounded-xl border border-purple-400/25 bg-purple-400/10 p-3">
                    <Combine className="h-8 w-8 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">Hybrid</h3>
                    <p className="text-sm font-medium text-purple-400 mb-2">Best of Both</p>
                    <p className="text-sm text-muted-foreground">
                      Use your own providers where you have deals, we provide the rest
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={() => setStep(2)}
                size="lg"
                className="brand-action rounded-xl px-12"
              >
                Continue
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Signup Form & Benefits */}
        {step === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Signup Form */}
            <Card className="bg-background border-border shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl text-center text-foreground">Create Your Account</CardTitle>
                <p className="text-center text-muted-foreground">Get started in less than 5 minutes</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleEmailSignup} className="space-y-5">
                  {error && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-sm text-red-400">{error}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="businessName">Business Name *</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="businessName"
                          value={formData.businessName}
                          onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
                          placeholder="Your Business Ltd."
                          className="pl-10 bg-card border-border text-foreground"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contactName">Contact Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="contactName"
                          value={formData.contactName}
                          onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                          placeholder="John Smith"
                          className="pl-10 bg-card border-border text-foreground"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="businessEmail">Business Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="businessEmail"
                        type="email"
                        value={formData.businessEmail}
                        onChange={(e) => setFormData(prev => ({ ...prev, businessEmail: e.target.value }))}
                        placeholder="you@yourbusiness.com"
                        className="pl-10 bg-card border-border text-foreground"
                        required
                      />
                    </div>
                    {domainError && (
                      <p className="text-sm text-red-500 mt-1">{domainError}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="website">Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="website"
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="https://yourbusiness.com"
                        className="pl-10 bg-card border-border text-foreground"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Business Description</Label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Tell us about your business and what you sell..."
                      className="flex min-h-[80px] w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fluxCyan-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      rows={3}
                    />
                  </div>

                  {/* Password fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="password">Password *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Min 8 characters"
                          className="pl-10 pr-10 bg-card border-border text-foreground"
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground/90 transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="confirmPassword">Confirm Password *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm password"
                          className="pl-10 pr-10 bg-card border-border text-foreground"
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground/90 transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Create Account button */}
                  <Button
                    type="submit"
                    disabled={loading || !!domainError}
                    className="w-full"
                    size="lg"
                  >
                    {loading ? "Creating account..." : "Create Account"}
                    {!loading && <ArrowRight className="h-4 w-4 ml-2" />}
                  </Button>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-3 bg-background text-muted-foreground">or</span>
                    </div>
                  </div>

                  {/* Google OAuth button */}
                  <button
                    type="button"
                    onClick={handleGoogleSignup}
                    disabled={googleLoading}
                    className="brand-inverse-action flex h-11 w-full items-center justify-center gap-3 rounded-xl font-semibold shadow-md transition-all duration-200 hover:shadow-lg disabled:opacity-50"
                  >
                    {googleLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        <span>Redirecting...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span>Continue with Google</span>
                      </>
                    )}
                  </button>

                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="text-sm text-muted-foreground hover:text-fluxCyan-400 transition-colors"
                    >
                      &larr; Back to mode selection
                    </button>
                  </div>

                  <p className="text-xs text-center text-muted-foreground">
                    By signing up, you agree to our{" "}
                    <a href="/terms" className="text-fluxCyan-400 hover:text-fluxCyan-300 underline underline-offset-2">Terms of Service</a>
                    {" "}and{" "}
                    <a href="/privacy" className="text-fluxCyan-400 hover:text-fluxCyan-300 underline underline-offset-2">Privacy Policy</a>
                  </p>
                </form>
              </CardContent>
            </Card>

            {/* Benefits */}
            <div className="space-y-6">
              <Card className="bg-background border-border shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center text-foreground">
                    <CreditCard className="h-5 w-5 text-fluxCyan-400 mr-2" />
                    Why Choose FluxPay?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-success mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">Competitive Rates</p>
                      <p className="text-sm text-muted-foreground">Industry-leading transaction fees starting at 2.4%</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-success mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">Fast Settlements</p>
                      <p className="text-sm text-muted-foreground">Get paid faster with T+1 to T+7 settlement options</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-success mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">Global Coverage</p>
                      <p className="text-sm text-muted-foreground">Accept payments from customers worldwide</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-success mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">24/7 Support</p>
                      <p className="text-sm text-muted-foreground">Round-the-clock support when you need it</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-success mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">Advanced Security</p>
                      <p className="text-sm text-muted-foreground">Bank-level security and fraud protection</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-background border-border shadow-xl">
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Shield className="h-12 w-12 text-fluxCyan-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Trusted by Growing Businesses
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Join 500+ businesses processing payments with FluxPay
                    </p>
                    <div className="flex justify-center space-x-8 text-sm text-muted-foreground">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-fluxCyan-400">500+</p>
                        <p>Merchants</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-fluxCyan-400">$150M+</p>
                        <p>Processed</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-success">99.9%</p>
                        <p>Uptime</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 text-muted-foreground">
          <p>Already have an account? <button
            onClick={() => router.push('/auth/signin')}
            className="text-fluxCyan-400 hover:text-fluxCyan-300 font-medium"
          >
            Sign in here
          </button></p>
        </div>
      </div>
    </div>
  )
}
